import React, { useState } from 'react';
import { Button, Modal } from 'antd';
import { BookIcon, CloseXIcon, LikeIcon, LocationIcon } from 'icons';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { calculateDiscount } from 'modules/global';

import { SuccessModal, Visible } from 'components';

import { useLeadSend } from '../hooks';
import { closeModal } from '../store';

import LeadModal from './lead-modal';

interface IProps {
  ticketId?: number;
}

const LearningCenterModal: React.FC<IProps> = ({ ticketId }) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const { mutate, isLoading } = useLeadSend();

  const isAuth = useAppSelector(({ user }) => user?.phone);
  const userName = useAppSelector(({ user }) => user?.name);
  const userPhone = useAppSelector(({ user }) => user?.phone);
  const selected = useAppSelector(state => state.learningCenterModal.selectedCenter);
  const open = useAppSelector(({ learningCenterModal }) => learningCenterModal?.isOpen);

  const [successModal, setSuccessModal] = useState(false);
  const [openLeadModal, setOpenLeadModal] = useState(false);

  const onAfterSuccess = () => {
    setSuccessModal(true);

    setTimeout(() => {
      setSuccessModal(false);
      dispatch(closeModal());
    }, 2000);
  };

  const sendLead = () => {
    if (!isAuth) {
      setOpenLeadModal(true);
      return;
    }

    const data = {
      name: userName,
      phone: userPhone,
      ticket_id: ticketId,
      learning_center_id: selected?.learning_center?.id
    };

    mutate(data, {
      onSuccess: onAfterSuccess
    });
  };

  return (
    <>
      <Modal
        centered
        open={open}
        width="866px"
        footer={null}
        closeIcon={null}
        className="relative"
        onCancel={() => dispatch(closeModal())}
        styles={{
          content: { padding: '16px', borderRadius: '16px 0 16px 16px' }
        }}
      >
        <div
          onClick={() => dispatch(closeModal())}
          className="rounded- absolute -top-5 right-0 cursor-pointer rounded-t-xl bg-white px-3 py-1"
        >
          <CloseXIcon color="#000" />
        </div>
        <div className="min-h-111 flex flex-col gap-4 md:flex-row">
          {/* Left content */}
          <div className="flex flex-col justify-between space-y-2 md:w-1/2">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <div className="relative h-9 w-9">
                  <Image
                    fill
                    alt="Inter Nation"
                    className="rounded-full object-cover"
                    src={selected?.learning_center?.logo || ''}
                  />
                </div>
                <p className="line-clamp-1 text-lg font-medium">{selected?.learning_center?.name?.toUpperCase()}</p>
              </div>
              <div className="mb-10">
                <h2 className="mb-2 text-xl font-bold">{selected?.learning_center?.title?.toUpperCase()}</h2>
                <p className="line-clamp-4 text-sm text-gray-600">{selected?.learning_center?.description}</p>
              </div>
            </div>

            <Visible if={selected?.address || selected?.subject || selected?.price || selected?.discount}>
              <div>
                <div className="relative mb-4 rounded-xl border border-gray-200 p-2">
                  {selected?.learning_center?.is_recommended ? (
                    <div className="absolute -top-5 left-3 flex items-center gap-1 rounded-t-[0.6rem] bg-orange-500 px-2 py-0.5 text-[0.6rem] text-white">
                      <LikeIcon /> {t('akam_tavsiya')}
                    </div>
                  ) : null}
                  <div className="mb-3 grid grid-cols-2 gap-3">
                    <div className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 px-2 py-1.5">
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-400">{t('auth.region_placeholder')}</span>
                        <span className="font-medium text-gray-900">{selected?.address?.name || '--'}</span>
                      </div>
                      <div className="text-gray-500">
                        <LocationIcon />
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 px-2 py-1.5">
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-400">{t('fanlar')}</span>
                        <span className="font-medium text-gray-900">{selected?.subject?.name || '--'}</span>
                      </div>
                      <div className="text-gray-500">
                        <BookIcon />
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-50 p-3">
                    <div className="flex items-end justify-between">
                      <p className="text-lg font-semibold">{selected?.subject?.name || '--'}</p>
                      <div className="flex flex-col items-end">
                        {selected?.discount ? (
                          <>
                            <span className="text-xs font-medium text-gray-400 line-through">
                              {(selected?.price || 0)?.toLocaleString('ru')} so’m
                            </span>
                            <div className="text-xl text-green-500">
                              <span className="font-semibold">
                                {(calculateDiscount(selected?.price, selected?.discount) || 0)?.toLocaleString('ru')}
                              </span>{' '}
                              <span className="font-xs ">so’m</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-xl text-green-500">
                            <span className="font-semibold">{(selected?.price || 0)?.toLocaleString('ru')}</span>
                            <span className="font-xs ">so’m</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  loading={isLoading}
                  onClick={sendLead}
                  className="!w-full !cursor-pointer !rounded-lg !bg-green-600 !py-5 !text-white hover:!bg-green-600"
                >
                  {t('bepul_darsga_yozilish')}
                </Button>
              </div>
            </Visible>
          </div>

          {/* Right image */}
          <div className="relative mt-5 hidden md:mt-0 md:block md:w-1/2">
            <Image
              src={selected?.learning_center?.image || ''} // Replace with actual image path
              alt="School"
              fill
              className="h-full w-full rounded-lg object-cover"
            />
            {selected?.discount ? (
              <div className="absolute right-0 top-0 rounded-bl-lg rounded-tr-lg bg-gradient-to-br from-red-300 to-red-600 px-2 py-0.5 text-xs text-white">
                -{selected?.discount}% {t('chegirma')}
              </div>
            ) : null}
          </div>
        </div>
      </Modal>

      <LeadModal
        ticketId={ticketId}
        learningCenterId={selected?.learning_center?.id}
        open={openLeadModal}
        onClose={() => setOpenLeadModal(false)}
        setSuccessVisible={setSuccessModal}
      />
      <SuccessModal
        open={successModal}
        onClose={() => setSuccessModal(false)}
        title={t('ariza_yuborildi')}
        desc={t('ariza_yuborildi_tavsif')}
      />
    </>
  );
};

export default LearningCenterModal;
